// Kâr-Zarar Tablosu sayfası kullanım rehberi. Metinler 'guides' çeviri alanında.
import type { TFunction } from 'i18next';
import type { GuideContent } from '../../components/common/UsageGuideDialog';

const G = 'finance.profitLoss';

export const profitLossGuide = (t: TFunction): GuideContent => ({
  layout: 'tabbed',
  title: t(`${G}.title`, 'Kâr-Zarar Tablosu nasıl okunur?'),
  intro: t(`${G}.intro`, 'Bu sayfa seçtiğiniz dönemde satışlarınızı, maliyetinizi ve giderlerinizi toplayıp işletmenin kâr mı zarar mı ettiğini gösterir. Haritada bir bölgeye tıklayarak o adıma geçebilirsiniz.'),
  navItems: [
    t(`${G}.nav.statement`, 'Gelir Tablosu'),
    t(`${G}.nav.branches`, 'Şubeler'),
    t(`${G}.nav.trend`, 'Aylık Trend'),
    t(`${G}.nav.breakdown`, 'Gider Dağılımı'),
    t(`${G}.nav.quality`, 'Veri Kalitesi'),
  ],
  regionLabels: {
    header: t(`${G}.regions.header`, 'Tarih, şube ve Raporu Oluştur'),
    tabs: t(`${G}.regions.tabs`, 'Sekmeler'),
    body: t(`${G}.regions.body`, 'Kartlar ve tablo'),
    footer: t(`${G}.regions.footer`, 'Excel / PDF / Yazdır'),
  },
  steps: [
    {
      region: 'header',
      title: t(`${G}.s1.title`, '1. Dönemi ve şubeleri seçip raporu oluşturun'),
      text: t(`${G}.s1.text`, 'Başlangıç ve Bitiş tarihini seçin ya da Bugün / Bu Ay / Geçen Ay / Bu Yıl kısayollarını kullanın. Şubeler alanından bir veya birkaç şube seçin; boş bırakırsanız Tüm Şubeler hesaplanır. Rapor yalnız "Raporu Oluştur" düğmesine basınca hesaplanır.'),
      tips: [
        t(`${G}.s1.tip1`, 'Tüm tutarlar KDV dahildir; KDV ayrıca düşülmez. Döviz faturaları faturadaki kurla TL\'ye çevrilir.'),
        t(`${G}.s1.tip2`, 'Tarih aralığı en fazla 366 gün olabilir.'),
        t(`${G}.s1.tip3`, 'Tüm Şubeler seçiliyken şubesi olmayan kayıtlar (Merkez) da dahildir. Şube seçerseniz şubesiz kayıtlar rapora girmez.'),
        t(`${G}.s1.tip4`, '"Gider Türleri" düğmesi gider türlerini ve hangi Kâr-Zarar grubuna yazılacaklarını düzenler; rapor açıksa pencere kapanınca yeni gruplamayla yeniden hesaplanır.'),
        t(`${G}.s1.tip5`, 'Şube yöneticisi olarak girdiyseniz şube sabittir; Merkez (şubesiz) kayıtlar, banka ve kredi kartı giderleri ile finansman giderleri rapora dahil değildir.'),
      ],
    },
    {
      region: 'body',
      title: t(`${G}.s2.title`, '2. Gelir Tablosu: satıştan net satışa'),
      text: t(`${G}.s2.text`, 'Brüt Satışlar; Hızlı Satış / Kasa, Restoran, Paket ve Platform Siparişleri (gün sonu fişleri) ile Satış Faturalarının toplamıdır. İadeler; kasa iadeleri ve satış iade faturalarıdır. NET SATIŞLAR = Brüt Satışlar − İadeler.'),
      tips: [
        t(`${G}.s2.tip1`, 'Bir kasa fişine bağlı kesilen satış faturası ikinci kez gelire eklenmez.'),
        t(`${G}.s2.tip2`, 'İptal edilen fişler gelire dahil edilmez; sayısı ve tutarı tablonun altındaki Bilgilendirme bölümünde yazar.'),
        t(`${G}.s2.tip3`, '"% Net Satış" sütunu her satırın net satışa oranıdır; net satış sıfır veya eksiyse "-" görünür.'),
      ],
    },
    {
      region: 'body',
      title: t(`${G}.s3.title`, '3. Maliyet, giderler ve net kâr nasıl hesaplanır?'),
      text: t(`${G}.s3.text`, 'Satılan Malın Maliyeti (SMM) = Alış Faturaları − Alış İade Faturaları. BRÜT KÂR = Net Satışlar − SMM. FAALİYET KÂRI = Brüt Kâr − Faaliyet Giderleri. DÖNEM NET KÂRI = Faaliyet Kârı − Finansman Giderleri. Satırların yanındaki ok ile alt kalemleri açabilir, tutarı olan bir satıra tıklayarak o tutarı oluşturan kayıtları görebilirsiniz.'),
      tips: [
        t(`${G}.s3.tip1`, 'Maliyet, stok hareketinden değil alış faturalarından hesaplanır. Gider türü seçilmemiş veya türü "Maliyet" grubunda olan alış faturaları SMM\'de kalır; Kira, Enerji gibi başka gruptaki türlü faturalar kendi gider satırına yazılır.'),
        t(`${G}.s3.tip2`, 'Faaliyet Giderleri: Personel (maaş / prim hakedişleri), Kira, Enerji, Pazarlama, Genel Yönetim, Diğer ve Kategorisiz. Kaynakları kasa giderleri, kredi kartı harcamaları, gider türü seçilmiş banka para çekmeleri ve türlü alış faturalarıdır.'),
        t(`${G}.s3.tip3`, 'Personel gideri hakediş esasıyla yazılır; hakedişe bağlanmamış personel ödemeleri (avans dahil) gidere yazılmaz.'),
        t(`${G}.s3.tip4`, 'Gider türü seçilmemiş kasa giderleri ve kart harcamaları "Kategorisiz (!)" satırına düşer. Gider türü seçilmeden yapılan kasa / banka para çekmeleri hiç gidere yazılmaz (cari, çek ve iade ödemeleri de bu yolla kaydedildiği için).'),
        t(`${G}.s3.tip5`, 'Finansman Giderleri: dönemde ödenen kredi taksitlerinin faiz payı ve gecikme faizi, kredi masrafları ve "Finansman" grubundaki türlü giderler.'),
      ],
    },
    {
      region: 'body',
      title: t(`${G}.s4.title`, '4. Özet kartları ve karşılaştırma'),
      text: t(`${G}.s4.text`, 'Üstteki kartlar Net Satış, Brüt Kâr (brüt marj %), Faaliyet Giderleri, Net Kâr (net marj %) ve Prime Cost\'u gösterir. Prime Cost = (SMM + Personel Giderleri) ÷ Net Satış. "Karşılaştır" alanından Önceki Dönem veya Geçen Yıl seçerseniz tabloya Önceki ve Değişim sütunları eklenir.'),
      tips: [
        t(`${G}.s4.tip1`, 'Brüt marj = Brüt Kâr ÷ Net Satış, net marj = Net Kâr ÷ Net Satış.'),
        t(`${G}.s4.tip2`, 'Tablonun altındaki Bilgilendirme kutuları rapora girmeyen veya eksik kayıtları (türsüz giderler, hakedişsiz ödemeler vb.) tutarıyla birlikte söyler.'),
      ],
    },
    {
      region: 'tabs',
      title: t(`${G}.s5.title`, '5. Şubeler sekmesi'),
      text: t(`${G}.s5.text`, 'Seçili dönemde her şubenin kâr-zarar tablosunu yan yana gösterir; üstteki grafik her şubenin net kârını çizer. Sekmeye geçip "Oluştur"a basın; son oluşturulan raporun tarih aralığı kullanılır.'),
      tips: [
        t(`${G}.s5.tip1`, 'Bu sekmede şube filtresi uygulanmaz: tüm şubeler, şubesiz kayıtlar için "Merkez (şubesiz)" sütunu ve Gelir Tablosu ile aynı olan "Toplam" sütunu gösterilir.'),
        t(`${G}.s5.tip2`, 'Şube yöneticisi görünümünde yalnız yetkili olduğunuz şubeler gösterilir; Merkez giderleri, banka, kredi kartı ve finansman satırları dahil değildir.'),
      ],
    },
    {
      region: 'tabs',
      title: t(`${G}.s6.title`, '6. Aylık Trend, Gider Dağılımı ve Veri Kalitesi'),
      text: t(`${G}.s6.text`, 'Her sekme kendi "Oluştur" düğmesiyle hesaplanır. Aylık Trend en fazla 12 ayı ay ay gösterir; her ayın değeri o ayın Gelir Tablosu ile aynıdır. Gider Dağılımı giderlerin gruplara dağılımını ve en büyük 10 gider kalemini verir. Veri Kalitesi raporu bozan eksik kayıtları listeler.'),
      tips: [
        t(`${G}.s6.tip1`, 'Veri Kalitesi örnekleri: gider türü seçilmemiş alış faturaları, türsüz kasa / kart giderleri, şubesiz faturalar, kuru girilmemiş döviz faturaları. Sekme başlığındaki sayı uyarı adedidir.'),
        t(`${G}.s6.tip2`, 'Ana "Raporu Oluştur" düğmesine yeniden basınca diğer sekmelerin eski sonuçları temizlenir.'),
      ],
    },
    {
      region: 'footer',
      title: t(`${G}.s7.title`, '7. Excel, PDF ve yazdırma'),
      text: t(`${G}.s7.text`, 'Rapor oluşturulunca başlığın sağında Excel, PDF ve Yazdır düğmeleri çıkar. Excel dosyasında Gelir Tablosu ve o ana kadar oluşturduğunuz her sekme ayrı bir sayfadır. PDF, Gelir Tablosunu ekrandaki açık / kapalı satırlarla aynı görünümde alır.'),
    },
  ],
});
